import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  YAxis,
  XAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { fetchUSRealYields, fetchRealSpreadOverlay } from "@/lib/api";
import { tooltipStyle, tooltipLabelStyle } from "@/lib/chartTheme";
import { useTheme } from "@/contexts/ThemeContext";

const bp = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(0)}bp`);
const sign = (v) =>
  v == null ? "text-zinc-500" : v > 0 ? "text-pos" : v < 0 ? "text-neg" : "text-zinc-500";

function YieldRow({ label, value, chg, accent = false, unit = "%" }) {
  const key = label.toLowerCase().replace(/[^a-z0-9]/g, "-");
  return (
    <div className="flex items-center justify-between border-b border-zinc-900 py-1.5">
      <span className={`text-[11px] uppercase tracking-widest ${accent ? "text-amber-term" : "text-zinc-500"}`}>
        {label}
      </span>
      <span className="flex items-baseline gap-2">
        <span
          className={`text-sm tabular-nums ${accent ? "text-amber-term font-semibold" : "text-zinc-100"}`}
          data-testid={`realyield-${key}`}
        >
          {value != null ? `${value.toFixed(2)}${unit}` : "—"}
        </span>
        {chg !== undefined && (
          <span className={`text-[11px] tabular-nums w-12 text-right ${sign(chg)}`}>{bp(chg)}</span>
        )}
      </span>
    </div>
  );
}

function Block({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-1 border-b border-zinc-800 pb-1">
        {title}
      </div>
      {children}
    </div>
  );
}

function SpreadStat({ label, value, chg, sub, testid, negColor = "#00BCD4", big = false }) {
  const premium =
    value == null ? "" : value > 0 ? "US premium" : value < 0 ? "DE premium" : "parity";
  const isNeg = value != null && value < 0;
  const valCls = value == null ? "text-zinc-100" : value > 0 ? "text-pos" : value < 0 ? "" : "text-zinc-100";
  const negStyle = isNeg ? { color: negColor } : undefined;
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className={`${big ? "text-3xl font-bold" : "text-xl font-semibold"} tabular-nums ${valCls}`} style={negStyle}>
          {value != null ? `${value > 0 ? "+" : ""}${value.toFixed(2)}` : "—"}
        </span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600">pp</span>
        <span className={`text-[11px] tabular-nums ${sign(chg)}`}>{bp(chg)} 1D</span>
        {premium && <span className={`text-[10px] uppercase tracking-widest ml-auto ${valCls}`} style={negStyle}>{premium}</span>}
      </div>
      {sub && <span className="text-[11px] tabular-nums text-zinc-600">{sub}</span>}
    </div>
  );
}

export default function USRealYieldsCard() {
  const q = useQuery({
    queryKey: ["us-real-yields", 180],
    queryFn: () => fetchUSRealYields(180),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  const d = q.data;
  const de = d?.germany;
  const sp = d?.real_spread;
  const spread = sp?.us_minus_de_10y;

  const ov = useQuery({
    queryKey: ["real-spread-overlay", 180],
    queryFn: () => fetchRealSpreadOverlay(180),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });
  const overlay = ov.data?.points || [];
  const corr = ov.data?.corr;
  const { theme } = useTheme();
  const spreadColor = theme === "light" ? "#215E61" : "#00BCD4";

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="us-real-yields-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-1">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400">
          ▍ US vs Germany Yields · 2Y/10Y/30Y · Nominal · Real · Curve
        </span>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600">
          {d?.stale ? "FRED · CACHED" : "FRED + ECB"}{d?.as_of ? ` · ${d.as_of}` : ""}
        </span>
      </div>

      {q.isLoading ? (
        <div className="h-[200px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
          Loading yields…
        </div>
      ) : q.isError ? (
        <div className="h-[200px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">
          Real-yields data temporarily unavailable
        </div>
      ) : (
        <div className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1">
            <Block title="US 2Y · Policy" testid="realyield-tenor-2Y">
              <YieldRow label="Nominal (DGS2)" value={d?.us2y?.nominal} chg={d?.us2y?.nominal_chg_1d} accent />
              <YieldRow label="2s10s Curve" value={d?.curve?.us_2s10s} chg={d?.curve?.chg_1d} unit="pp" />
              <div className="pt-1 text-[11px] uppercase tracking-widest" data-testid="realyield-curve-note">
                {d?.curve == null ? (
                  <span className="text-zinc-600">curve n/a</span>
                ) : (
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 border ${
                      d.curve.level === "neg"
                        ? "text-neg border-red-500/50"
                        : d.curve.level === "warn"
                        ? "text-amber-term border-amber-500/40"
                        : d.curve.level === "pos"
                        ? "text-pos border-emerald-500/50"
                        : "text-zinc-500 border-zinc-700"
                    }`}
                    title={`2s10s ${d.curve.us_2s10s}pp, ${(d.curve.chg_1w * 100).toFixed(0)}bp 1w — ${d.curve.tone}`}
                    data-testid="realyield-curve-badge"
                  >
                    {d.curve.regime} · {d.curve.tone}
                  </span>
                )}
              </div>
            </Block>

            <Block title="US 10Y" testid="realyield-tenor-10Y">
              <YieldRow label="Nominal (DGS)" value={d?.tenors?.["10Y"]?.nominal} chg={d?.tenors?.["10Y"]?.nominal_chg_1d} />
              <YieldRow label="Real · TIPS (DFII)" value={d?.tenors?.["10Y"]?.real} chg={d?.tenors?.["10Y"]?.real_chg_1d} accent />
              <YieldRow label="Breakeven" value={d?.tenors?.["10Y"]?.breakeven} />
            </Block>

            <Block title="US 30Y" testid="realyield-tenor-30Y">
              <YieldRow label="Nominal (DGS)" value={d?.tenors?.["30Y"]?.nominal} chg={d?.tenors?.["30Y"]?.nominal_chg_1d} />
              <YieldRow label="Real · TIPS (DFII)" value={d?.tenors?.["30Y"]?.real} chg={d?.tenors?.["30Y"]?.real_chg_1d} accent />
              <YieldRow label="Breakeven" value={d?.tenors?.["30Y"]?.breakeven} />
            </Block>

            <Block title="Germany 10Y" testid="realyield-tenor-DE">
              <YieldRow label="Nominal (Bund)" value={de?.nominal_10y} chg={de?.nominal_chg_1d} />
              <YieldRow label="Real · ex-post" value={de?.real_10y} accent />
              <YieldRow label="HICP YoY" value={de?.cpi_yoy} />
            </Block>
          </div>

          {/* US–DE rate differential — nominal vs real side by side + FX read */}
          <div className="mt-3 border border-zinc-800 bg-black/40 p-3" data-testid="realyield-spread">
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2 border-b border-zinc-800 pb-1">
              US–DE 10Y Rate Differential · EUR/USD driver
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <SpreadStat
                label="Nominal 10Y"
                value={sp?.us_minus_de_10y_nominal}
                chg={sp?.nominal_chg_1d}
                sub={sp?.us_nominal_10y != null ? `US ${sp.us_nominal_10y}% − DE ${sp.de_nominal_10y}%` : null}
                testid="realyield-spread-nominal"
                negColor={spreadColor}
              />
              <SpreadStat
                label="Real 10Y (ex-post)"
                value={spread}
                chg={sp?.chg_1d}
                sub={sp?.us_real_expost_10y != null ? `US ${sp.us_real_expost_10y}% − DE ${sp.de_real_expost_10y}%` : null}
                testid="realyield-spread-real"
                negColor={spreadColor}
                big
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] uppercase tracking-widest text-zinc-500">Market real (TIPS)</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-semibold tabular-nums text-amber-term">
                    {sp?.us_real_market_10y != null ? `${sp.us_real_market_10y.toFixed(2)}%` : "—"}
                  </span>
                  <span className="text-[11px] uppercase tracking-widest text-zinc-600">US DFII10</span>
                </div>
                <span className="text-[11px] tabular-nums text-zinc-600">vs ex-post real {sp?.us_real_expost_10y != null ? `${sp.us_real_expost_10y}%` : "—"}</span>
              </div>
            </div>
          </div>

          <div className="mt-3 mb-1 flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-zinc-600">
              US–DE 10Y Real Spread vs EUR/USD · ~6M
            </span>
            <span className="flex items-center gap-3 text-[11px] uppercase tracking-widest">
              {corr != null && <span className="text-zinc-500">corr {corr > 0 ? "+" : ""}{corr}</span>}
              <span className="flex items-center gap-1 text-amber-term">
                <span className="inline-block h-[2px] w-3 bg-amber-term" /> Spread (pp)
              </span>
              <span className="flex items-center gap-1 text-fuchsia-400">
                <span className="inline-block h-[2px] w-3 bg-fuchsia-400" /> EUR/USD
              </span>
            </span>
          </div>
          <div className="h-[150px] -mx-1" data-testid="realyield-overlay-chart">
            {ov.isLoading ? (
              <div className="h-full flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
                Loading overlay…
              </div>
            ) : overlay.length < 3 ? (
              <div className="h-full flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
                Overlay unavailable
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={overlay} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#18181B" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#52525B", fontSize: 9, fontFamily: "JetBrains Mono" }}
                    stroke="#27272A"
                    tickFormatter={(d) => (d ? d.slice(5) : "")}
                    minTickGap={44}
                  />
                  <YAxis
                    yAxisId="spread"
                    tick={{ fill: spreadColor, fontSize: 9, fontFamily: "JetBrains Mono" }}
                    stroke="#27272A"
                    width={38}
                    tickFormatter={(v) => v.toFixed(1)}
                    domain={["dataMin - 0.05", "dataMax + 0.05"]}
                  />
                  <YAxis
                    yAxisId="fx"
                    orientation="right"
                    tick={{ fill: "#E879F9", fontSize: 9, fontFamily: "JetBrains Mono" }}
                    stroke="#27272A"
                    width={46}
                    tickFormatter={(v) => v.toFixed(3)}
                    domain={["dataMin - 0.005", "dataMax + 0.005"]}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelStyle={tooltipLabelStyle}
                    formatter={(v, n) => [n === "EUR/USD" ? v?.toFixed(4) : `${v?.toFixed(2)}pp`, n]}
                  />
                  <Line yAxisId="spread" type="monotone" dataKey="spread" name="US–DE Spread" stroke={spreadColor} strokeWidth={1.5} dot={false} />
                  <Line yAxisId="fx" type="monotone" dataKey="eurusd" name="EUR/USD" stroke="#E879F9" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div
            className="mt-3 border-l-2 border-amber-500 bg-amber-500/5 px-3 py-2"
            data-testid="realyield-read"
          >
            <div className="text-[11px] uppercase tracking-widest text-amber-term/80 mb-1">Daily Read</div>
            <p className="text-xs text-zinc-200 leading-relaxed">{d?.read}</p>
            {d?.curve && (
              <p
                className={`mt-1 text-[11px] leading-relaxed ${
                  d.curve.level === "neg" ? "text-neg" : d.curve.level === "warn" ? "text-amber-term" : d.curve.level === "pos" ? "text-pos" : "text-zinc-500"
                }`}
                data-testid="realyield-curve-cue"
              >
                2s10s {d.curve.us_2s10s > 0 ? "+" : ""}{d.curve.us_2s10s}pp ({d.curve.chg_1w >= 0 ? "+" : ""}{(d.curve.chg_1w * 100).toFixed(0)}bp 1w) · {d.curve.regime} — {d.curve.tone}.
              </p>
            )}
            {sp?.note && (
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-600">{sp.note}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
